import './Styleguide.css';
import React from 'react';
import { Container } from '../../components/Container/Container';
import { StyleguideSection } from './StyleguideSection';
import { ButtonExample } from '../../components/Button/ButtonExample';
import { CardExample } from '../../components/Card/CardExample';
import { FormExample } from '../../components/Form/FormExample';
import { IconExample } from '../../components/Icon/IconExample';
import { ModalExample } from '../../components/Modal/ModalExample';
import { PaginationExample } from '../../components/Pagination/PaginationExample';
import { SelectableListExample } from '../../components/SelectableList/SelectableListExample';
import { SortableListExample } from '../../components/SortableList/SortableListExample';
import { SpinnerExample } from '../../components/Spinner/SpinnerExample';
import { TabsExample } from '../../components/Tabs/TabsExample';
import { TagExample } from '../../components/Tag/TagExample';
import { AvatarUploadExample } from '../../components/AvatarUpload/AvatarUploadExample';
import { KeyValueTableExample } from '../../components/KeyValueTable/KeyValueTableExample';
import { FileUploadExample } from '../../components/Form/FileUpload/FileUploadExample';
import { TooltipExample } from '../../components/Tooltip/TooltipExample';
import { ChartsExample } from '../../components/Charts/ChartsExample';
import { ListSearchExample } from '../../components/ListSearch/ListSearchExample';
import { useMeta } from '../../model/meta';
import { PromptExample } from '../../components/ConfirmationPrompt/PromptExample';
import { StatusIconExample } from '../../components/StatusIcon/StatusIconExample';
import { ProductImageExample } from '../../components/ProductImage/ProductImageExample';
import { SortableListMultiselectExample } from '../../components/SortableList/SortableListMultiselectExample';
import { NotificationExample } from '../../components/Notifications/NotificationExample';
import { SalesDonut } from '../../components/SalesDonut/SalesDonut';
import { LabelledProgress } from '../../components/LabelledProgress/LabelledProgress';
import { TagsGroupExample } from '../../components/Tag/TagsGroupExample';
import { Stack } from '../../components/Stack/Stack';
import { HeatmapExample } from '../../components/Heatmap/HeatmapExample';
import { DonutWithBars } from '../../components/DonutWithBars/DonutWithBars';
import { t } from '../../locales/translate';
import { IntervalField } from '../../components/IntervalField/IntervalField';
import { LineAreaChartExample } from '../../components/Charts/LineAreaChartExample';

export function Styleguide(): JSX.Element {
  useMeta({ title: 'Styleguide' });
  return (
    <Container className="styleguide">
      <header className="styleguide__header">
        <h1>Styleguide</h1>
      </header>
      <Stack withSeparator size="lg" className="styleguide__body">
        <StyleguideSection title="LineArea chart">
          <LineAreaChartExample />
        </StyleguideSection>

        <StyleguideSection title="Interval field">
          <IntervalField
            initialValue={{ from: new Date(new Date().setDate(-30)), to: new Date() }}
            onChange={(v) =>
              alert(
                JSON.stringify(
                  { from: v?.from?.toLocaleString(), to: v?.to?.toLocaleString(), period: v?.period },
                  null,
                  2
                )
              )
            }
            value={{ from: new Date(new Date().setDate(-30)), to: new Date(), period: 'day' }}
          />
        </StyleguideSection>

        {/* AVATAR UPLOAD */}
        <StyleguideSection title="Avatar Upload">
          <AvatarUploadExample />
        </StyleguideSection>
        {/* BUTTONS */}
        <StyleguideSection title="Buttons">
          <ButtonExample />
        </StyleguideSection>
        {/* CARDS */}
        <StyleguideSection title="Cards">
          <CardExample />
        </StyleguideSection>
        {/* FORM */}
        <StyleguideSection title="Form">
          <FormExample />
        </StyleguideSection>
        {/* ICON */}
        <StyleguideSection title="Icons">
          <IconExample />
        </StyleguideSection>
        {/* KEY VALUE TABLE */}
        <StyleguideSection title="Key Value Table">
          <KeyValueTableExample />
        </StyleguideSection>
        {/* MODAL */}
        <StyleguideSection title="Modal">
          <ModalExample />
        </StyleguideSection>
        {/* Prompt */}
        <StyleguideSection title="Confirmation Prompt">
          <PromptExample />
        </StyleguideSection>
        {/* Pagination */}
        <StyleguideSection title="Pagination">
          <PaginationExample />
        </StyleguideSection>
        {/* Selectable List */}
        <StyleguideSection title="Selectable List">
          <SelectableListExample />
        </StyleguideSection>
        {/* Sortable List */}
        <StyleguideSection title="Sortable List">
          <SortableListExample />
        </StyleguideSection>
        {/* Sortable List Multiselect */}
        <StyleguideSection title="Sortable List with Multiselect">
          <SortableListMultiselectExample />
        </StyleguideSection>
        {/* Spinner */}
        <StyleguideSection title="Spinner">
          <SpinnerExample />
        </StyleguideSection>
        {/* Tabs */}
        <StyleguideSection title="Tabs">
          <TabsExample />
        </StyleguideSection>
        {/* tooltip */}
        <StyleguideSection title="Tooltip">
          <TooltipExample />
        </StyleguideSection>
        {/* Tags */}
        <StyleguideSection title="Tags">
          <TagExample />
        </StyleguideSection>
        {/* Tags Group */}
        <StyleguideSection title="Tags Group">
          <TagsGroupExample />
        </StyleguideSection>
        {/* Charts */}
        <StyleguideSection title="Area Chart">
          <ChartsExample />
        </StyleguideSection>
        {/* List Search */}
        <StyleguideSection title="List Search">
          <ListSearchExample />
        </StyleguideSection>
        {/* Status Icon */}
        <StyleguideSection title="Status Icons">
          <StatusIconExample />
        </StyleguideSection>
        {/* File Upload */}
        <StyleguideSection title="File Upload">
          <FileUploadExample />
        </StyleguideSection>
        {/* Product Image */}
        <StyleguideSection title="Product Image">
          <ProductImageExample />
        </StyleguideSection>
        {/* Error Catcher */}
        <StyleguideSection title="Notifications">
          <NotificationExample />
        </StyleguideSection>
        <StyleguideSection title="Sales State">
          <SalesDonut
            data={{
              available: 300,
              local: 10,
              blocked: 0,
              cart: 0,
              reserved: 0,
              sold: 0,
              soldHard: 0,
              soldOnline: 0,
              personalized: 0,
              systemFree: 2,
              systemSold: 100,
              used: 5,
              processing: 2,
              returned: null,
            }}
            size="medium"
          />
        </StyleguideSection>
        <StyleguideSection title="Labelled ProgressBar">
          <LabelledProgress
            values={[
              {
                value: 30,
                label: 'Value 1',
              },
              {
                value: 60,
                label: 'Value 2',
              },
            ]}
          />
        </StyleguideSection>
        {/* Heatmap */}
        <StyleguideSection title="Heatmap">
          <HeatmapExample />
        </StyleguideSection>
        <StyleguideSection title="Donut with Bars">
          <DonutWithBars
            data={[
              { name: t.general.genders.soldToMUsers, value: 10, color: '#2d9cdb' },
              { name: t.general.genders.soldToFUsers, value: 15, color: '#eb5757' },
              { name: t.general.genders.soldToXUsers, value: 20, color: '#676767' },
            ]}
          />
        </StyleguideSection>
      </Stack>
    </Container>
  );
}
